import { useUserProfile } from '@/context/UserProfileContext';
import { capitalizeFirstLetter } from '@/lib/utils';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar
} from '@ionic/react';
import { logoFacebook, logoInstagram, logoLinkedin, logoTiktok, logoTwitter, logoYoutube } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AvatarUpload from './AvatarUpload';
import CountrySelect from './CountrySelect';
import DexterityPicker from './DexterityPicker';
import { FloatingSaveButton } from './FloatingSaveButton';
import GolfHandicapInput from './GolfHandicapInput';
import SexPicker from './SexPicker';
import TeeSelect from './TeeSelect';
import YearPicker from './YearPicker';
import { api } from '@/utils/api';
import type { Profile } from './UserSearch';

const EditProfileForm = () => {
  const { profile, refreshProfile } = useUserProfile();

  // Create a state to track if form has been initialized with profile data
  const [formInitialized, setFormInitialized] = useState(false);

  // Define form types
  type SocialLink = { platform: string; url: string };

  type FormValues = {
    firstName: string;
    lastName: string;
    description: string;
    countryCode: string;
    sex: number;
    handicap: number | null;
    golfStartYear: string;
    tee: string;
    dexterity: string;
    socialLinks: SocialLink[];
  };

  const defaultSocialLinks: SocialLink[] = [
    { platform: 'facebook', url: '' },
    { platform: 'instagram', url: '' },
    { platform: 'youtube', url: '' },
    { platform: 'linkedin', url: '' },
    { platform: 'twitter', url: '' },
    { platform: 'tiktok', url: '' },
  ];

  // Get default values based on profile or empty values
  const getDefaultValues = () => {
    if (!profile) {
      return {
        firstName: '',
        lastName: '',
        description: '',
        countryCode: '',
        sex: null,
        handicap: null,
        golfStartYear: null,
        tee: 'white',
        dexterity: 'right',
        socialLinks: defaultSocialLinks,
      };
    }

    // Cast profile to ExtendedProfile to access the additional fields
    const extendedProfile = profile as unknown as FormValues;

    const getSocialLinksFromProfile = (profile: any) => {
      return [
        { platform: 'facebook', url: profile.facebookLink || '' },
        { platform: 'instagram', url: profile.instagramLink || '' },
        { platform: 'youtube', url: profile.youtubeLink || '' },
        { platform: 'linkedin', url: profile.linkedinLink || '' },
        { platform: 'twitter', url: profile.twitterLink || '' },
        { platform: 'tiktok', url: profile.tiktokLink || '' },
      ];
    }

    return {
      firstName: extendedProfile.firstName || '',
      lastName: extendedProfile.lastName || '',
      description: extendedProfile.description || '',
      countryCode: extendedProfile.countryCode || '',
      sex: extendedProfile.sex || 0,
      handicap: extendedProfile.handicap !== undefined ? Number(extendedProfile.handicap) : null,
      golfStartYear: extendedProfile.golfStartYear || null,
      tee: extendedProfile.tee || 'white',
      dexterity: extendedProfile.dexterity || 'right',
      socialLinks: extendedProfile ? getSocialLinksFromProfile(extendedProfile) : defaultSocialLinks,
    };
  };

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  // Update form values when profile changes
  useEffect(() => {
    if (profile && !formInitialized) {
      // Set default values once when profile is loaded
      reset(getDefaultValues(), { keepValues: false });
      setFormInitialized(true);
    }
  }, [profile, formInitialized, reset]); // Re-run when profile changes

  const [showToast, setShowToast] = useState({ message: '', color: '' });
  const { t } = useTranslation();
  const { isDirty } = formState;
  const onSubmit = async (data: any) => {
    try {
      if (!profile?.username) {
        throw new Error('Username not found');
      }

      // Create a copy of the data to prevent form reset issues
      const formData = { ...data };

      // Show loading toast
      setShowToast({ message: 'Updating profile...', color: 'primary' });

      console.log(formData);
      const response = await api.post(`/profile/${profile.username}`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        description: formData.description,
        countryCode: formData.countryCode,
        sex: formData.sex,
        handicap: formData.handicap,
        golfStartYear: formData.golfStartYear,
        tee: formData.tee,
        dexterity: formData.dexterity,
        socialLinks: formData.socialLinks,
        // The server expects a Date object, not a string
        updatedAt: new Date()
      });

      if (response.status === 200 || response.status === 201) {
        setShowToast({ message: 'Profile updated successfully!', color: 'success' });
        refreshProfile();
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setShowToast({ message: 'Failed to update profile. Please try again.', color: 'danger', });
    }
  };

  if (!profile)
    return '';
  return (
    <>
      <IonHeader>
        <IonToolbar className="md:hidden bg-card">
          <IonButtons slot="start">
            <IonBackButton text={t('back')} defaultHref="/settings" />
          </IonButtons>
          <IonTitle>{t('edit_profile')}</IonTitle>
          <IonButtons slot="end">
            <IonButton type='submit' disabled={formState.isSubmitting || !isDirty} onClick={handleSubmit(onSubmit)}>
              {t('save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-no-padding">

        <form className='ion-padding pb-16' onSubmit={handleSubmit(onSubmit)}>
          <h1 className="hidden text-3xl pt-6 font-bold md:block">{t('edit_profile')}</h1>
          <div className="my-4 flex">
            <AvatarUpload initialImage={profile?.imageUrl} size="lg" />
          </div>

          <IonList lines="none" className='space-y-4'>
            {/* First and Last Name */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('first_name')}</IonLabel>
              <IonItem className="custom-item" no-lines>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <IonInput
                      onIonInput={(e) => field.onChange(e.target.value)}
                      placeholder="Enter your first name"
                      {...field}
                    />
                  )}
                />
              </IonItem>
            </div>

            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('last_name')}</IonLabel>
              <IonItem className="custom-item">
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <IonInput
                      onIonInput={(e) => field.onChange(e.target.value)}
                      placeholder="Enter your last name"
                      {...field}
                    />
                  )}
                />
              </IonItem>
            </div>

            {/* Description */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">Bio</IonLabel>
              <IonItem className="custom-item">
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <IonTextarea
                      placeholder={t('MGP:whats_up') + '?!'}
                      onIonInput={(e) => field.onChange(e.target.value)}
                      rows={4}
                      {...field}
                    />
                  )}
                />
              </IonItem>
            </div>

            {/* Country */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('country')}</IonLabel>
              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => (
                  <CountrySelect
                    defaultValue={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Sex */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('sex')}</IonLabel>
              <Controller
                name="sex"
                control={control}
                render={({ field }) => (
                  <SexPicker
                    defaultValue={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Handicap */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('golf_handicap')}</IonLabel>
              <Controller
                name="handicap"
                control={control}
                render={({ field }) => (
                  <GolfHandicapInput initialValue={field.value || 0} onChange={field.onChange} />
                )}
              />
            </div>

            {/* Golf Start Year */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('golf_start_year')}</IonLabel>
              <Controller
                name="golfStartYear"
                control={control}
                render={({ field }) => (
                  <YearPicker
                    defaultValue={field.value}
                    onChange={field.onChange}
                    placeholder={t('select_year')}
                    startYear={1945}
                    endYear={new Date().getFullYear()}
                  />
                )}
              />
            </div>

            {/* Dexterity (Left or Right Handed) */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('dexterity')}</IonLabel>
              <Controller
                name="dexterity"
                control={control}
                render={({ field }) => (
                  <DexterityPicker
                    defaultValue={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            {/* Tee */}
            <div className="mb-4 space-y-2">
              <IonLabel className="block">{t('tee')}</IonLabel>
              <Controller
                name="tee"
                control={control}
                render={({ field }) => (
                  <TeeSelect defaultValue={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            {/* Social Links */}
            <div className="pb-safe-offset-8 space-y-2">
              <IonLabel className="block text-lg font-semibold">{t('social_medias')}</IonLabel>
              {getValues('socialLinks').map((link: SocialLink, index: number) => (
                <IonItem className="custom-item mb-2" key={index}>
                  <IonIcon
                    className='size-5'
                    icon={
                      (() => {
                        const socialIcons = {
                          facebook: logoFacebook,
                          instagram: logoInstagram,
                          youtube: logoYoutube,
                          linkedin: logoLinkedin,
                          twitter: logoTwitter,
                          tiktok: logoTiktok,
                        };
                        return socialIcons[link.platform as keyof typeof socialIcons];
                      })()
                    }
                    slot="start"
                  />
                  <Controller
                    name={`socialLinks.${index}.url`}
                    control={control}
                    render={({ field }) => (
                      <IonInput
                        onIonInput={(e) => field.onChange(e.target.value)}
                        placeholder={`${capitalizeFirstLetter(link.platform)}`}
                        {...field}
                      />
                    )}
                  />
                </IonItem>
              ))}
            </div>
          </IonList>
          {/* Cancel and Save Buttons */}
          {isDirty && (
            <div className='fixed md:block hidden inset-x-0 bottom-safe-offset-10'>
              <FloatingSaveButton state={isDirty ? "initial" : "loading"} onReset={() => reset()} onSave={handleSubmit(onSubmit)} />
            </div>
          )}
        </form>

        {/* Toast */}
        <IonToast
          isOpen={!!showToast.message}
          message={showToast.message}
          duration={2000}
          position="top"
          color={showToast.color}
          onDidDismiss={() => setShowToast({ message: '', color: '' })}
        />
      </IonContent>
    </>
  );
};

export default EditProfileForm;